import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Checkbox, FormControlLabel, FormGroup,
   Button, Card, Grid, Divider } from '@mui/material';

const ProjectForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    companyName: '',
    companyOverview: '',
    websiteOrLinkedIn: '',
    projectTitle: '',
    projectSummary: '',
    domain: [],
    expectedDeliverables: '',
    preferredTechStack: '',
    preferredProgrammingLanguages: '',
    otherDomain: '',
  });

  
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));

    if (formErrors[field]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleDomainChange = (event) => {
    const { value, checked } = event.target;
    setForm((prevForm) => {
      const selected = prevForm.domain || [];
      return {
        ...prevForm,
        domain: checked
          ? [...selected, value]
          : selected.filter((v) => v !== value),
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    if (!form.companyName) errors.companyName = 'Company name is required';
    if (!form.companyOverview) errors.companyOverview = 'Company overview is required';
    if (!form.websiteOrLinkedIn) errors.websiteOrLinkedIn = 'Website or LinkedIn is required';
    if (!form.projectTitle) errors.projectTitle = 'Project title is required';
    if (!form.projectSummary) errors.projectSummary = 'Project summary is required';
    if (form.domain.length === 0) errors.domain = 'At least one domain must be selected';
    if (form.domain.includes('Other') && !form.otherDomain) errors.otherDomain = 'Other domain is required';
    if (!form.preferredTechStack) errors.preferredTechStack = 'Preferred tech stack is required';
    if (!form.preferredProgrammingLanguages) errors.preferredProgrammingLanguages = 'Preferred programming languages are required';
    if (!form.expectedDeliverables) errors.expectedDeliverables = 'Expected deliverables are required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    localStorage.setItem('projectFormData', JSON.stringify(form));
    navigate('/organizations/post-project');
  };

  useEffect(() => {
    const stored = localStorage.getItem('projectFormData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setForm((prev) => ({
          ...prev,
          ...parsed,
          domain: Array.isArray(parsed.domain) ? parsed.domain : [],
        }));
      } catch (error) {
        //console.error('Error parsing stored form data:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      const firstErrorKey = Object.keys(formErrors)[0];
      const element = document.querySelector(`[name="${firstErrorKey}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formErrors]);

  useEffect(() => {
    const token = localStorage.getItem('company_token');
    if (token) {
      const companyNameFromToken = decodeCompanyNameFromToken(token);
      if (companyNameFromToken) {
        setForm((prev) => ({ ...prev, companyName: companyNameFromToken }));
      }
    }
  }, []);

  const decodeCompanyNameFromToken = (token) => {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      return decoded.companyName || '';
    } catch (error) {
      console.error('Error decoding token:', error);
      return '';
    }
  };

  return (
    <div>
      <Box sx={{padding:"20px", justifyContent:"center", display:"flex", alignItems:"center"}}>
      <Card
        sx={{
          width : '100%',
          maxWidth : 900,
          p: 4,
          boxShadow: 6,
          borderRadius: 4,
          backgroundColor: '#ffff',
        }}
      >
        <Box sx={{ maxWidth : 900, width : '100%' }}>
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ fontSize: { xs: '1rem', sm: '1.4rem' } }}
            gutterBottom
            textAlign="center"
          >
            Submit Your Project
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            color="text.secondary"
            mb={4}
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            Provide detailed information about your company project.
          </Typography>

          {/* Company Info */}
          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 2 }}
          >
            Company Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid item xs={12} marginBottom="20px">
            <TextField
              fullWidth
              name="companyName"
              label="1. Company Name"
              value={form.companyName}
              InputProps={{ readOnly: true }}
              error={!!formErrors.companyName}
              helperText={formErrors.companyName}
            />
          </Grid>
          <Grid container spacing={3}>
            {[
              {
                name: 'companyOverview',
                label: '2. Company Overview',
                placeholder: 'Brief introduction: domain, size, mission, etc.',
                multiline: true,
                rows: 4
              },
              {
                name: 'websiteOrLinkedIn',
                label: '3. Website / LinkedIn Profile',
                placeholder: 'Enter website or LinkedIn URL',
                sm: 6
              },
              {
                name: 'projectTitle',
                label: '4. Project Title',
                placeholder: 'Concise and specific title',
                sm: 6
              },
            ].map(({ name, label, placeholder, multiline = false, rows = 1, sm = 12 }) => (
              <Grid item xs={12} sm={sm} key={name}>
                <TextField
                  fullWidth
                  name={name}
                  label={label}
                  value={form[name]}
                  onChange={handleChange(name)}
                  error={!!formErrors[name]}
                  helperText={formErrors[name]}
                  placeholder={placeholder}
                  multiline={multiline}
                  rows={rows}
                  InputLabelProps={{
                    sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                  }}
                  inputProps={{
                    sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                  }}
                />
              </Grid>
            ))}
          </Grid>

          <Typography
            variant="h6"
            fontWeight={600}
            sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, mt: 4 }}
          >
            2. Project Overview
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="projectSummary"
                label="5. Project Summary"
                value={form.projectSummary}
                onChange={handleChange('projectSummary')}
                error={!!formErrors.projectSummary}
                helperText={formErrors.projectSummary}
                placeholder="Describe the problem, context, and objective in 3–5 sentences"
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
                inputProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, mt: 2 }}
              >
                6. Domain / Industry
              </Typography>
              <FormGroup row>
                {[
                  'IoT',
                  'Embedded Systems',
                  'Data Science',
                  'AI/ML',
                  'Web/Mobile Development',
                  'Cybersecurity',
                  'Other',
                ].map((domainItem) => (
                  <FormControlLabel
                    key={domainItem}
                    control={
                      <Checkbox
                        value={domainItem}
                        checked={form.domain.includes(domainItem)}
                        onChange={handleDomainChange}
                      />
                    }
                    label={domainItem}
                    sx={{ mt: 2 }}
                  />
                ))}
              </FormGroup>
              {form.domain.includes('Other') && (
                <Grid item xs={12} mt={2}>
                  <TextField
                    fullWidth
                    label="Specify Other Domain"
                    value={form.otherDomain}
                    onChange={handleChange('otherDomain')}
                    error={!!formErrors.otherDomain}
                    helperText={formErrors.otherDomain}
                    placeholder="Enter custom domain"
                    InputLabelProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                    inputProps={{
                      sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                    }}
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                label="7. Preferred Tech Stack / Tools"
                value={form.preferredTechStack}
                onChange={handleChange('preferredTechStack')}
                error={!!formErrors.preferredTechStack}
                helperText={formErrors.preferredTechStack}
                placeholder="e.g., ESP32, Python, TensorFlow, Node.js"
                rows={4}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
                inputProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                label="8. Preferred Programming Languages"
                value={form.preferredProgrammingLanguages}
                onChange={handleChange('preferredProgrammingLanguages')}
                error={!!formErrors.preferredProgrammingLanguages}
                helperText={formErrors.preferredProgrammingLanguages}
                placeholder="Specify if any (e.g., Python, JavaScript)"
                rows={4}
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
                inputProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="9. Expected Deliverables"
                value={form.expectedDeliverables}
                onChange={handleChange('expectedDeliverables')}
                error={!!formErrors.expectedDeliverables}
                helperText={formErrors.expectedDeliverables}
                placeholder="Dashboards, reports, algorithms, hardware, etc."
                InputLabelProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
                inputProps={{
                  sx: { fontSize: { xs: '0.85rem', sm: '1rem' } },
                }}
              />
            </Grid>
          </Grid>

          <Box
            textAlign="center"
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap="20px"
            sx={{ mb: 4, mt: 5 }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={() => {
                setForm({
                  companyName: '',
                  companyOverview: '',
                  websiteOrLinkedIn: '',
                  projectTitle: '',
                  projectSummary: '',
                  domain: [],
                  expectedDeliverables: '',
                  preferredTechStack: '',
                  preferredProgrammingLanguages: '',
                  otherDomain: '',
                });
                setFormErrors({});
                localStorage.removeItem('projectFormData');
              }}
              sx={{
                px: { xs: 3, sm: 6 },
                py: { xs: 1, sm: 1.5 },
                fontSize: { xs: '0.85rem', sm: '1rem' },
                fontWeight: 600,
                color: 'rgb(2, 41, 100)',
                borderColor: 'rgb(2, 41, 100)',
              }}
            >
              Clear Form
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSubmit}
              sx={{
                px: { xs: 3, sm: 6 },
                py: { xs: 1, sm: 1.5 },
                fontSize: { xs: '0.85rem', sm: '1rem' },
                fontWeight: 600,
              }}
            >
              Save & Continue
            </Button>
          </Box>
        </Box>
      </Card>
      </Box>
    </div>
  );
};

export default ProjectForm;
